import React, { useMemo, useState, useEffect } from "react";
import { mediaUrl } from "@/lib/api";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { inr, api } from "@/lib/api";

function SizePicker({ sizes, size, setSize }) {
  if (!sizes) return null;
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-biga-ink/50 mb-2">Size</p>
      <div className="grid grid-cols-2 gap-2">
        {sizes.map((s) => (
          <button key={s.label} data-testid={`size-${s.label}`} onClick={() => setSize(s)}
            className={`px-3 py-3 rounded-xl border-2 text-left transition-colors ${size?.label === s.label ? "border-biga-orange bg-biga-orange/5" : "border-biga-ink/12"}`}>
            <span className="block text-sm font-bold">{s.label}</span>
            <span className="text-biga-orange font-display font-bold">{inr(s.price)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ExtrasList({ toppings, extras, toggleExtra }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-biga-ink/50 mb-2">Extra toppings</p>
      <div className="space-y-2">
        {toppings.extras.map((ex) => (
          <label key={ex.id} className="flex items-center justify-between px-4 py-2.5 rounded-xl border-2 border-biga-ink/10 cursor-pointer hover:border-biga-orange transition-colors">
            <span className="flex items-center gap-3 font-semibold text-sm">
              <input type="checkbox" data-testid={`extra-${ex.id}`} checked={extras.includes(ex.id)} onChange={() => toggleExtra(ex.id)} className="w-4 h-4 accent-biga-orange" />
              {ex.name}
            </span>
            <span className="font-bold text-biga-green text-sm">{ex.price ? `+${inr(ex.price)}` : "Free"}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function HalfAndHalfPicker({ item, otherPizzas, halfAndHalf, setHalfAndHalf }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-biga-ink/50 mb-2">Make it half & half (optional)</p>
      <div data-testid="half-half-select" role="radiogroup" aria-label="Half and half choice" className="grid gap-2">
        <button type="button" onClick={() => setHalfAndHalf("")} aria-pressed={!halfAndHalf} className={`w-full text-left px-4 py-3 rounded-xl border-2 font-semibold text-sm ${!halfAndHalf ? "border-biga-orange bg-biga-orange/5" : "border-biga-ink/12"}`}>Keep it full {item.name}</button>
        {otherPizzas.map((pizza) => <button type="button" key={pizza.id} onClick={() => setHalfAndHalf(pizza.name)} aria-pressed={halfAndHalf === pizza.name} className={`w-full text-left px-4 py-3 rounded-xl border-2 font-semibold text-sm ${halfAndHalf === pizza.name ? "border-biga-orange bg-biga-orange/5" : "border-biga-ink/12"}`}>Half {item.name} / Half {pizza.name}</button>)}
      </div>
    </div>
  );
}

export default function CustomizeModal({ item, allItems, onClose, onConfirm }) {
  const [toppings, setToppings] = useState({ extras: [] });
  const [size, setSize] = useState(item?.sizes?.[0] || null);
  const crust = "BIGA Crust"; // silent, automatic crust on every pizza — never shown to the customer
  const [extras, setExtras] = useState([]);
  const [halfAndHalf, setHalfAndHalf] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get("/menu/toppings").then((r) => setToppings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (item) {
      setSize(item.sizes?.[0] || null);
      setExtras([]);
      setHalfAndHalf("");
      setQty(1);
    }
  }, [item]);

  const extrasTotal = useMemo(
    () => extras.reduce((sum, id) => sum + (toppings.extras.find((e) => e.id === id)?.price || 0), 0),
    [extras, toppings]
  );

  const unitPrice = (size ? size.price : item?.price || 0) + extrasTotal;

  const toggleExtra = (id) =>
    setExtras((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const confirm = (e) => {
    const extraLabels = extras.map((id) => toppings.extras.find((x) => x.id === id)?.name).filter(Boolean);
    const key = [item.id, size?.label, crust, extras.sort().join("-"), halfAndHalf].join("|");
    onConfirm(
      {
        key,
        item_id: item.id,
        name: item.name,
        size: size?.label,
        crust,
        extras: extraLabels,
        half_and_half: halfAndHalf || null,
        qty,
        unit_price: unitPrice,
        image: item.image,
      },
      e
    );
  };

  const otherPizzas = (allItems || []).filter((i) => i.category === "roman" && i.id !== item?.id);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          key="customize-modal"
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          data-testid="customize-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
        >
          <motion.div
            className="absolute inset-0 bg-biga-ink/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="relative w-full sm:max-w-lg bg-biga-cream rounded-t-3xl sm:rounded-3xl border-2 border-biga-ink max-h-[90vh] overflow-y-auto no-scrollbar"
          >
            <div className="relative h-40 overflow-hidden rounded-t-[1.4rem] sm:rounded-t-3xl">
              <img src={mediaUrl(item.image, 640)} alt={item.name} width={600} height={400} decoding="async" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-biga-ink/70 to-transparent" />
              <button onClick={onClose} data-testid="customize-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-biga-cream flex items-center justify-center" aria-label="Close customization">
                <X size={18} />
              </button>
              <div className="absolute bottom-3 left-5 right-5">
                <h2 className="font-display font-black text-3xl text-biga-cream leading-none">{item.name}</h2>
                {item.category === "roman" && <p className="text-xs font-medium text-biga-cream/75 mt-1 tracking-wide" data-testid="dough-line">72-hour fermented BIGA dough</p>}
              </div>
            </div>

            <div className="p-5 space-y-5">
              <SizePicker sizes={item.sizes} size={size} setSize={setSize} />
              <ExtrasList toppings={toppings} extras={extras} toggleExtra={toggleExtra} />
              <HalfAndHalfPicker item={item} otherPizzas={otherPizzas} halfAndHalf={halfAndHalf} setHalfAndHalf={setHalfAndHalf} />
            </div>

            <div className="sticky bottom-0 bg-biga-cream border-t-2 border-biga-ink/10 p-4 flex items-center gap-3">
              <div className="flex items-center gap-3 border-2 border-biga-ink/15 rounded-full px-2 py-1">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="cust-qty-minus" className="w-8 h-8 flex items-center justify-center"><Minus size={16} /></button>
                <span className="font-bold w-5 text-center" data-testid="cust-qty">{qty}</span>
                <button onClick={() => setQty(qty + 1)} data-testid="cust-qty-plus" className="w-8 h-8 flex items-center justify-center"><Plus size={16} /></button>
              </div>
              <button
                onClick={confirm}
                data-testid="confirm-add-btn"
                className="flex-1 bg-biga-orange text-biga-cream font-bold py-3.5 rounded-full hover:bg-biga-green transition-colors flex items-center justify-center gap-2"
              >
                Add {qty} {"\u2022"} {inr(unitPrice * qty)}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
